import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActionResult, HelperFunctions } from 'bowman-commons';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { RateLimitAdjustRequest } from 'src/app/security/model/rate-limit-adjust-request';
import { RateLimitConfig, RateLimitConfigUpdateRequest } from 'src/app/security/model/rate-limit-config';
import { RateLimitGroupTuning } from 'src/app/security/model/rate-limit-group-tuning';
import { RateLimitStatus } from 'src/app/security/model/rate-limit-status';
import { RateLimitMe } from 'src/app/services/rate-limit/model/rate-limit-me';

@Injectable({
  providedIn: 'root'
})
export class RateLimiterService {
  private _mainUrl: string = window.location.pathname + '/api/rateLimiter/';
  headers: HttpHeaders = new HttpHeaders();

  constructor(private _http: HttpClient) {
    this.headers = new HttpHeaders().set('Content-Type', 'application/json').set('Accept', 'application/json');
  }

  public getMe(): Observable<ActionResult<RateLimitMe>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'me');
    return this._http.get<ActionResult<RateLimitMe>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public getConfig(): Observable<ActionResult<RateLimitConfig>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'config');
    return this._http.get<ActionResult<RateLimitConfig>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public saveConfig(request: RateLimitConfigUpdateRequest): Observable<ActionResult<RateLimitConfig>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'config');
    return this._http.put<ActionResult<RateLimitConfig>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public resetRuntimeConfig(): Observable<ActionResult<RateLimitConfig>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'config/runtime');
    return this._http.delete<ActionResult<RateLimitConfig>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public list(): Observable<ActionResult<Array<RateLimitStatus>>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'list');
    return this._http.get<ActionResult<Array<RateLimitStatus>>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public get(username: string): Observable<ActionResult<RateLimitStatus>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'user/' + encodeURIComponent(username));
    return this._http.get<ActionResult<RateLimitStatus>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public reset(username: string): Observable<ActionResult<RateLimitStatus>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'user/' + encodeURIComponent(username) + '/reset');
    return this._http.put<ActionResult<RateLimitStatus>>(dataUrl, {}, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public setTokens(username: string, request: RateLimitAdjustRequest): Observable<ActionResult<RateLimitStatus>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'user/' + encodeURIComponent(username) + '/tokens');
    return this._http.put<ActionResult<RateLimitStatus>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public remove(username: string): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'user/' + encodeURIComponent(username));
    return this._http.delete<ActionResult<boolean>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public flush(): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'flushAll');
    return this._http.delete<ActionResult<boolean>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public getTuningForGroup(groupId: string): Observable<ActionResult<RateLimitGroupTuning>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'tuning/group/' + encodeURIComponent(groupId));
    return this._http.get<ActionResult<RateLimitGroupTuning>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  private handleError(error: any) {
    return observableThrowError(error);
  }
}
